import { dmnIdentity } from "../../utils/dmnIdentity";

interface AdoptionTelemetryTokenResponse {
  access_token?: string;
  expires_in?: number;
  expires_at?: number;
}

export interface AdoptionTelemetryTokenProvider {
  getToken: () => Promise<string | undefined>;
  invalidate: () => void;
}

const EXPIRY_SKEW_MS = 60_000;
const EPOCH_MILLISECONDS_THRESHOLD = 1e12;
const MILLISECONDS_PER_SECOND = 1000;
const DEFAULT_TOKEN_LIFETIME_MS = 5 * 60_000;

const resolveExpiryTime = (
  response: AdoptionTelemetryTokenResponse,
  now: number
): number => {
  if (typeof response.expires_in === "number") {
    return now + response.expires_in * MILLISECONDS_PER_SECOND;
  }

  if (typeof response.expires_at === "number") {
    return response.expires_at < EPOCH_MILLISECONDS_THRESHOLD
      ? response.expires_at * MILLISECONDS_PER_SECOND
      : response.expires_at;
  }

  return now + DEFAULT_TOKEN_LIFETIME_MS;
};

const fetchAdoptionTelemetryToken = async (
  authApiBaseUrl: string
): Promise<AdoptionTelemetryTokenResponse | undefined> => {
  const dmnToken = await dmnIdentity.getAccessToken();
  if (!dmnToken) return undefined;

  const response = await fetch(`${authApiBaseUrl}/workspace/telemetry/token`, {
    method: "GET",
    headers: { Authorization: `Bearer ${dmnToken}` }
  });

  if (!response.ok) return undefined;

  return (await response.json()) as AdoptionTelemetryTokenResponse;
};

/**
 * Exchanges the DMN access token for an Access telemetry bearer token
 * (same auth-api route as the legacy collins-ui) and caches it until expiry.
 * Never throws: telemetry must not affect the app.
 */
export const createAdoptionTelemetryTokenProvider = (
  authApiBaseUrl: string
): AdoptionTelemetryTokenProvider => {
  let cachedToken: string | undefined;
  let cachedTokenExpiresAt = 0;

  const invalidate = () => {
    cachedToken = undefined;
    cachedTokenExpiresAt = 0;
  };

  const getToken = async (): Promise<string | undefined> => {
    if (cachedToken && Date.now() < cachedTokenExpiresAt - EXPIRY_SKEW_MS) {
      return cachedToken;
    }

    try {
      const response = await fetchAdoptionTelemetryToken(authApiBaseUrl);
      cachedToken = response?.access_token ?? undefined;
      cachedTokenExpiresAt = response
        ? resolveExpiryTime(response, Date.now())
        : 0;
      return cachedToken;
    } catch {
      invalidate();
      return undefined;
    }
  };

  return { getToken, invalidate };
};
